/**
 * Global "Update Ready" toast wrapper.
 *
 * This wraps UpdateReadyPrompt to handle:
 * - Subscribing to update events from main process
 * - Fetching initial state on mount
 */

import { useEffect } from 'react';
import { useAutoUpdatesStore } from '../../../store/autoUpdatesStore';
import type { AutoUpdateEvent } from '../../../types/autoUpdates';
import { UpdateReadyPrompt } from './UpdateReadyPrompt';

/** Check if we're in a desktop (Electron) environment */
function isDesktop(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('Electron') &&
    !!window.electronAPI?.autoUpdates
  );
}

export function UpdateReadyToast() {
  const { setState, handleEvent } = useAutoUpdatesStore();

  // Subscribe to update events from main process
  useEffect(() => {
    if (!isDesktop()) return;

    const unsubscribe = window.electronAPI!.autoUpdates.onEvent(
      (event: AutoUpdateEvent) => {
        handleEvent(event);
      }
    );

    return unsubscribe;
  }, [handleEvent]);

  // Fetch initial state on mount
  useEffect(() => {
    if (!isDesktop()) return;

    const fetchState = async () => {
      try {
        const initialState = await window.electronAPI!.autoUpdates.getState();
        setState(initialState);
      } catch (error) {
        console.error('[UpdateReadyToast] Failed to fetch initial state:', error);
      }
    };

    fetchState();
  }, [setState]);

  // Don't render in web builds
  if (!isDesktop()) return null;

  return <UpdateReadyPrompt />;
}
